import axios from 'axios';
import { getExpiraEm, isTokenValid, removeSession, sessaoSemPermissoes, setSession } from './auth';

// O JWT viaja em cookie httpOnly: o navegador anexa sozinho (withCredentials),
// nenhum script lê o token. X-Client identifica o front web para o back emitir
// o cookie em vez de devolver o token no corpo. Como a sessão é por cookie,
// o back exige CSRF token nas mutações — o axios copia PATIO-XSRF para o header.
// O cookie tem nome próprio, e não o XSRF-TOKEN padrão, porque navegadores
// compartilham cookies entre portas do mesmo host: em localhost o nome default
// colidia com resquício de build antiga e com cookie de outra ferramenta, e o
// valor lido pelo JS deixava de bater com o que o back recebia (403 intermitente).
const api = axios.create({
    baseURL: '/api',
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: 'PATIO-XSRF',
    xsrfHeaderName: 'X-XSRF-TOKEN',
    headers: { 'X-Client': 'web' },
});

// Sessão deslizante: quando o token está a menos de 2h de expirar,
// renova em segundo plano na próxima resposta bem-sucedida.
const RENOVAR_ANTES_MS = 2 * 60 * 60 * 1000;
let renovandoToken = false;
let inicializandoSessao = null;

const METODOS_MUTAVEIS = new Set(['post', 'put', 'patch', 'delete']);

const temCookieCsrf = () => typeof document !== 'undefined'
    && document.cookie.split('; ').some(cookie => cookie.startsWith('PATIO-XSRF='));

const renovarSePreciso = (urlChamada) => {
    if (renovandoToken || urlChamada?.includes('/auth/')) return;
    const expiraEm = getExpiraEm();
    if (!expiraEm) return;
    const restanteMs = expiraEm - Date.now();
    if (restanteMs <= 0 || restanteMs > RENOVAR_ANTES_MS) return;
    renovandoToken = true;
    api.post('/auth/renovar')
        .then(({ data }) => setSession(data))
        .catch(() => {})
        .finally(() => { renovandoToken = false; });
};

// Request: o cookie httpOnly já vai anexado. Se a sessão local existe mas já
// expirou (checagem client-side), faz logout local antes de disparar a chamada.
api.interceptors.request.use(async (config) => {
    const temSessao = getExpiraEm() > 0;
    if (temSessao && !isTokenValid() && !config.url?.includes('/auth/login')) {
        removeSession();
        if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
        }
        return Promise.reject(new axios.Cancel('Sessão expirada.'));
    }

    const metodo = config.method?.toLowerCase();
    if (METODOS_MUTAVEIS.has(metodo)
        && !config.url?.includes('/auth/login')
        && !temCookieCsrf()) {
        // Fecha a corrida entre a montagem da tela e o bootstrap assíncrono do
        // CSRF: nenhuma mutação sai enquanto o cookie ainda não foi emitido.
        await inicializarSessao();
    }

    return config;
});

// Response: renova o token perto de expirar; 401 fora do login derruba a sessão local.
api.interceptors.response.use(
    (response) => {
        renovarSePreciso(response.config?.url);
        return response;
    },
    async (error) => {
        if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
            removeSession();
            if (!window.location.pathname.includes('/login')) {
                window.location.href = '/login';
            }
            return Promise.reject(error);
        }

        // Token CSRF vencido ou ausente: qualquer GET faz o back reemitir o cookie
        // PATIO-XSRF, então busca um novo e repete a chamada uma única vez. Sem
        // isso a única saída era atualizar a página no meio da operação — e o erro
        // ainda dizia "permissão insuficiente", culpando quem tinha a permissão.
        const config = error.config;
        if (error.response?.status === 403
            && error.response?.data?.codigo === 'PATIO-AUTH-008'
            && config && !config._csrfRepetido) {
            config._csrfRepetido = true;
            try {
                await api.get('/auth/me');
                return await api(config);
            } catch (novoErro) {
                return Promise.reject(novoErro);
            }
        }

        return Promise.reject(error);
    }
);

// Sessão criada por versão antiga do front (sem permissões): busca /auth/me
// e recarrega para os guards enxergarem as permissões.
export const inicializarSessao = () => {
    if (inicializandoSessao) return inicializandoSessao;

    inicializandoSessao = api.get('/auth/me')
        .then(({ data }) => {
            const precisaRecarregar = sessaoSemPermissoes();
            if (precisaRecarregar) setSession(data);
            return { precisaRecarregar };
        })
        .finally(() => { inicializandoSessao = null; });

    return inicializandoSessao;
};

export const hidratarSessaoSeNecessario = async () => {
    try {
        const { precisaRecarregar } = await inicializarSessao();
        if (precisaRecarregar) window.location.reload();
    } catch {
        // 401 já derruba a sessão no interceptor
    }
};

// Corpo de erro do back (ver a página "Catálogo de erros" na wiki do repositório):
// { timestamp, status, codigo, message, path, traceId, erros: [{campo, codigo, mensagem}] }

// Código próprio do erro (ex.: PATIO-FILA-004). Null quando a falha é de rede.
export const codigoErro = (error) => error?.response?.data?.codigo || null;

// Erros de validação indexados por campo, para o formulário destacar o input:
// { placa: 'Placa é obrigatória', motoristaId: 'Motorista é obrigatório' }
export const errosPorCampo = (error) => {
    const lista = error?.response?.data?.erros;
    if (!Array.isArray(lista)) return {};
    return lista.reduce((acc, e) => {
        if (e?.campo && !acc[e.campo]) acc[e.campo] = e.mensagem || e.message || '';
        return acc;
    }, {});
};

// Extrai mensagem amigável do padrão de erro do back-end.
export const mensagemErro = (error, padrao = 'Ocorreu um erro inesperado.') => {
    const data = error?.response?.data;
    if (!data) return padrao;
    if (typeof data === 'string') return data;

    let base = data.mensagem || data.message;
    if (!base && Array.isArray(data.erros) && data.erros.length) {
        base = data.erros.map((e) => e.mensagem || e.message || e).join('; ');
    }
    if (!base && Array.isArray(data) && data.length) {
        base = data.map((e) => e.mensagem || e.message || e).join('; ');
    }
    if (!base) return padrao;

    // Erro interno não diz nada ao usuário: mostra o código e o traceId para
    // ele repassar ao suporte, que acha a linha exata no log do back.
    if (data.status === 500 && data.traceId) {
        return `${base} (${data.codigo || 'PATIO-SIS-003'} · ${data.traceId})`;
    }
    return base;
};

export default api;
